import React from "react"
import { Icon } from "../icon"
import { Tooltip } from "../tooltip"
import VisuallyHidden from "../visually-hidden"
import { cn } from "../utils"
import { useNavigationContext } from "./navigation-context"
import { NavigationTooltip } from "./navigation-tooltip"

export type NavigationCommandTriggerProps = Omit<
  React.ComponentPropsWithoutRef<"button">,
  "children"
> & {
  /** Placeholder-style label shown in the expanded trigger (and the tooltip / accessible name when collapsed). */
  placeholder?: string
  /** Keys rendered as a shortcut hint on the right, joined by "+". */
  keys?: string[]
}

// Mirrors NavigationLink's box so the trigger collapses like a nav row: the
// icon sits in a fixed `auto` grid column (pinned as the sidebar width
// animates) and the label fills `1fr`. The border stays 1px and only fades to
// transparent when collapsed, so there's no width jump.
const triggerStyles = [
  "grid grid-cols-[auto_1fr] items-center gap-x-2",
  "min-w-10 w-full h-10 rounded-[10px] p-2 border",
  "text-left text-sm text-gray-700 select-none",
  "hover:bg-gray-900/[0.06]",
  "outline-2 outline-transparent -outline-offset-2 focus-visible:outline-gray-900",
  "transition-colors"
].join(" ")

/**
 * A search-field-styled button for opening a command palette, sized for the
 * sidebar. It shares `Navigation.Item`'s collapse behaviour: the search icon
 * stays pinned while the label and shortcut hint clip away as the sidebar
 * narrows, and a label tooltip appears on hover when collapsed. It owns no
 * palette state; pass `onClick` to open one, keeping the design system
 * decoupled from any specific command menu.
 *
 * Place it directly inside `Navigation` (it reads the collapsed state from
 * context and includes its own sidebar spacing).
 *
 * @example
 * <Navigation.CommandTrigger onClick={() => setOpen(true)} />
 */
export const NavigationCommandTrigger = React.forwardRef<
  HTMLButtonElement,
  NavigationCommandTriggerProps
>(function NavigationCommandTrigger(
  { placeholder = "Search Kit", keys = ["⌘", "K"], className, ...props },
  ref
) {
  const { collapsed } = useNavigationContext()

  const trigger = (
    <button
      ref={ref}
      type="button"
      data-slot="navigation-command-trigger"
      className={cn(
        triggerStyles,
        collapsed ? "border-transparent" : "border-gray-300",
        className
      )}
      {...props}
    >
      <Icon name="search" className="shrink-0 text-gray-700" aria-hidden />
      {collapsed ? (
        <VisuallyHidden>{placeholder}</VisuallyHidden>
      ) : (
        <span className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 flex-1 truncate leading-6">
            {placeholder}
          </span>
          <span className="flex shrink-0 items-center gap-0.5" aria-hidden>
            {keys.map((key, index) => (
              <React.Fragment key={key}>
                {index > 0 && (
                  <span className="text-[11px] font-semibold text-black">
                    +
                  </span>
                )}
                <kbd className="flex size-4 items-center justify-center rounded-[4px] bg-gray-900/[0.06] text-[11px] font-semibold leading-4 text-black">
                  {key}
                </kbd>
              </React.Fragment>
            ))}
          </span>
        </span>
      )}
    </button>
  )

  return (
    <div className="px-2 pb-2">
      {/* Tooltip mirrors Navigation.Item: enabled only when collapsed, since the
          expanded field already shows the label. */}
      <Tooltip.Provider delay={0}>
        <Tooltip disabled={!collapsed}>
          <Tooltip.Trigger render={trigger} />
          <Tooltip.Content position="right" sideOffset={12} maxWidth={192}>
            <NavigationTooltip label={placeholder} />
          </Tooltip.Content>
        </Tooltip>
      </Tooltip.Provider>
    </div>
  )
})

NavigationCommandTrigger.displayName = "Navigation.CommandTrigger"
